import { WalletAccount, TxSet } from '@exodus/models'
// eslint-disable-next-line no-restricted-imports -- TODO: Fix this the next time the file is edited.
import lodash from 'lodash'
import { mapValues } from '@exodus/basic-utils'
import { isNumberUnit } from '@exodus/currency'
import { deserializeBalances, serializeBalances } from '../atoms/synced-balances.js'
import makeConcurrent from 'make-concurrent'

const { isEqualWith, get, throttle } = lodash

const balancesChannel = {
  type: 'balances',
  channelName: 'v2_balances',
  syncStateKey: 'sync:syncstate:v2_balances',
  replayLast: true,
  unencrypted: true,
}

const isTxLogsEqualCustomizer = (objValue, othValue) => {
  if (objValue instanceof TxSet && othValue instanceof TxSet) {
    return objValue === othValue
  }
}

const getIsTxLogsEqual = (txLogA, txLogB) => isEqualWith(txLogA, txLogB, isTxLogsEqualCustomizer)

const numberUnitEqualityCustomizer = (objValue, othValue) => {
  if (isNumberUnit(objValue) && isNumberUnit(othValue) && objValue.equals(othValue)) {
    return true
  }
}

const filterZeroBalances = ({ current, previous }) =>
  mapValues(current, (balancesByAssetName, accountName) =>
    Object.fromEntries(
      Object.entries(balancesByAssetName).filter(([assetName, balance]) => {
        if (balance.isZero) {
          const previousBalance = get(previous, [accountName, assetName])
          return previousBalance && !previousBalance.isZero
        }

        return true
      })
    )
  )

const mergeBalances = ({ current, previous }) =>
  Object.entries(current).reduce(
    (result, [accountName, balancesByAssetName]) => {
      if (!result[accountName]) {
        result[accountName] = balancesByAssetName
        return result
      }

      result[accountName] = {
        ...result[accountName],
        ...balancesByAssetName,
      }
      return result
    },
    { ...previous }
  )

const filterPayload = ({ payload, enabledWalletAccounts }) =>
  Object.fromEntries(
    Object.entries(payload).filter(([accountName]) => enabledWalletAccounts[accountName])
  )

const THROTTLE_PUSH_MS_DEFAULT = 2000

class SyncedBalances {
  #channel = null
  #syncedBalancesAtom = null
  #balancesAtom = null
  #enabledWalletAccountsAtom = null
  #fusion = null
  #getShouldIgnoreBalancesUpdate = null
  #previousTxLogs = null
  #loaded = false
  #observed = false
  #fusionDataSaved = false
  #subscriptions = []
  #logger
  #txLogsAtom

  constructor({
    fusion,
    balancesAtom, // If not passed (DEPRECATED), balance changes should be tracked in client and `syncedBalances.update` function should be called.
    syncedBalancesAtom,
    enabledWalletAccountsAtom,
    logger,
    getShouldIgnoreBalancesUpdate = async () => false, // A function returning promise resolved to a flag which would prevent handling of balances update.
    config: { throttlePushMs } = {}, // throttles updates to fusion
    txLogsAtom,
  }) {
    this.#syncedBalancesAtom = syncedBalancesAtom
    this.#balancesAtom = balancesAtom
    this.#enabledWalletAccountsAtom = enabledWalletAccountsAtom
    this.#fusion = fusion
    this.#getShouldIgnoreBalancesUpdate = getShouldIgnoreBalancesUpdate
    this.#logger = logger
    this.#txLogsAtom = txLogsAtom

    this.update = throttle(this.update, throttlePushMs || THROTTLE_PUSH_MS_DEFAULT)
  }

  load = () => {
    if (this.#loaded) {
      return
    }

    this.#loaded = true

    this.#channel = this.#fusion.channel({
      ...balancesChannel,
      processOne: async ({ data }) => {
        try {
          const balances = deserializeBalances(data)
          const balancesToSave = await this.#mergeAndFilter(balances)

          const previousBalances = await this.#safeSyncedBalancesAtomGet()
          const balancesEqual = isEqualWith(
            balancesToSave,
            previousBalances,
            numberUnitEqualityCustomizer
          )
          if (!balancesEqual) {
            await this.#syncedBalancesAtom.set(balancesToSave)
          }

          this.#fusionDataSaved = true
          this.#logger.debug('balances updated from fusion')
        } catch (err) {
          this.#logger.error('error synced-balances fusion update', err)
        }
      },
    })
  }

  observeBalances = () => {
    if (this.#observed) {
      return
    }

    this.#observed = true

    if (this.#balancesAtom) {
      this.#subscriptions.push(
        this.#balancesAtom.observe(async ({ balances }) => {
          const shouldIgnoreBalancesUpdate = await this.#getShouldIgnoreBalancesUpdate()
          if (shouldIgnoreBalancesUpdate) {
            return
          }

          const balancesNormalized = mapValues(balances, (balanceByAssetName) =>
            mapValues(balanceByAssetName, ({ balance }) => balance)
          )
          await this.update(balancesNormalized)
        })
      )
    }
  }

  #mergeAndFilter = async (balances) => {
    const previous = await this.#safeSyncedBalancesAtomGet()
    const enabledWalletAccounts = await this.#enabledWalletAccountsAtom.get()
    const payload = mergeBalances({ current: balances, previous, enabledWalletAccounts })
    return filterPayload({ payload, enabledWalletAccounts })
  }

  #getChannel = async () => {
    await this.#channel.awaitProcessed()

    return this.#channel
  }

  #safeSyncedBalancesAtomGet = async () =>
    this.#fusionDataSaved ? this.#syncedBalancesAtom.get() : { [WalletAccount.DEFAULT_NAME]: {} }

  /**
   * @deprecated should be used only if impossible to pass `balances` dependency to constructor
   * @typedef {import('@exodus/currency').default} NumberUnit
   * todo: make private when `balances` module introduced to desktop and mobile
   * @param {Record<string, Record<string, NumberUnit>> } balances Balances keyed by account name and then by asset name
   */
  update = makeConcurrent(
    async (balances) => {
      const currentTxLogs = await this.#txLogsAtom.get()
      const isTxLogsEqual = getIsTxLogsEqual(this.#previousTxLogs, currentTxLogs)
      if (isTxLogsEqual) {
        this.#logger.debug('skip push balances to fusion as tx logs did not change')
        return
      }

      this.#previousTxLogs = currentTxLogs

      const channel = await this.#getChannel()
      const previousBalances = await this.#safeSyncedBalancesAtomGet()

      // todo remove when `balances` module introduced to desktop and mobile
      const filteredBalances = filterZeroBalances({ current: balances, previous: previousBalances })
      const balancesToSave = await this.#mergeAndFilter(filteredBalances)

      if (isEqualWith(balancesToSave, previousBalances, numberUnitEqualityCustomizer)) {
        this.#logger.debug('skip push balances to fusion as they are the same')
        return
      }

      await this.#syncedBalancesAtom.set(balancesToSave)

      const serialized = serializeBalances(balancesToSave)
      await channel.push({
        type: balancesChannel.type,
        data: serialized,
      })
      this.#logger.debug('pushed balances to fusion')
    },
    { concurrency: 1 }
  )

  clear = async () => {
    await this.#syncedBalancesAtom.set(undefined)
  }

  stop = () => {
    this.#subscriptions.forEach((unsubscribe) => unsubscribe())
    this.#subscriptions = []
  }
}

const createSyncedBalances = (args) => new SyncedBalances({ ...args })

const syncedBalancesDefinition = {
  id: 'syncedBalances',
  type: 'module',
  factory: createSyncedBalances,
  dependencies: [
    'fusion',
    'balancesAtom?',
    'syncedBalancesAtom',
    'logger',
    'config',
    'enabledWalletAccountsAtom',
    'getShouldIgnoreBalancesUpdate?',
    'txLogsAtom',
  ],
  public: true,
}

export default syncedBalancesDefinition
